import { Component, input, inject, output, computed } from '@angular/core';
import { CommunityItem, PostTypes, POST_TYPE_ICON, FeedType } from '../../../../interfaces';
import { IconComponent, IconName } from '@outlaw/icons';
import { PostAvatarComponent } from '../post-avatar/post-avatar.component';
import { PostSocialLinkComponent } from '../post-social-link/post-social-link.component';
import { TitleCasePipe, NgTemplateOutlet } from '@angular/common';
import { UnixDateFormatterPipe } from '../../../../pipes/unix-date-formatter.pipe';
import { DataTestDirective, NavigationService, User } from '@outlaw/core';
import { GtagCommunityService } from '../../../../services/analytics/gta-community.service';
import { AppPlatformService } from '../../../../services/app-platform.service';
import { EXTERNAL_PROFILE_SERVICE } from '../../../../providers/external-profile.provider';
import { COMMUNITY_NAVIGATION_CONFIG } from '../../../../providers/community-navigation-config.provider';
import { ExternalPostNavigationService } from '../../../../services/navigation/external-post-navigation.service';
import { take } from 'rxjs/operators';
import { SOCIAL_PLATFORM_HEADER_TEXTS, POST_TYPE_TEXT } from '../../constants/post-header.constants';
import { CommunityMobileEventsService } from '../../../../services/util/mobile-events.service';

@Component({
  selector: 'app-post-header',
  templateUrl: './post-header.component.html',
  styleUrls: ['./post-header.component.scss'],
  imports: [
    PostAvatarComponent,
    PostSocialLinkComponent,
    TitleCasePipe,
    UnixDateFormatterPipe,
    NgTemplateOutlet,
    DataTestDirective,
    IconComponent,
  ],
})
export class PostHeaderComponent {
  item = input.required<CommunityItem>();
  isGuest = input<boolean>(false);
  isPostPinned = input<boolean>(false);
  isFollowEvent = input<boolean>(false);
  feedType = input<FeedType>(FeedType.user);
  profileLink = input<string>('');
  openInSameWindow = input<boolean>(false);
  enableExternalProfileLink = input<boolean>(false);
  isMajiApp = input<boolean>(false);
  user = input<User>();

  socialClick = output<string>();
  ownerTitleClick = output<void>();
  logoClick = output<void>();

  private gtagCommunityService = inject(GtagCommunityService);
  private navigationService = inject(NavigationService);
  private postSocialService = inject(AppPlatformService);
  private mobileEventsService = inject(CommunityMobileEventsService);
  #externalProfileService = inject(EXTERNAL_PROFILE_SERVICE, {
    optional: true,
  });
  #navConfig = inject(COMMUNITY_NAVIGATION_CONFIG, { optional: true });
  #externalPostNavService = inject(ExternalPostNavigationService);

  isClickableProfile = computed(
    () => !this.profileLink() && this.enableExternalProfileLink() && !!this.item().user?.user_uuid
  );

  userLogoUrl = computed(() => {
    if (!this.#navConfig?.showUserLogo) return null;
    return this.#externalPostNavService.getUserLogoUrl(this.item()?.user?.user_logo);
  });

  userHomepageUrl = computed(() => {
    if (!this.#navConfig?.useExternalPostNavigation) return null;
    return this.item()?.user?.user_homepage || null;
  });

  shouldDisplayPinned = computed(() => this.isPostPinned() && this.feedType() === FeedType.user);

  sourceLogo = computed(() => this.postSocialService.getSourceLogo(this.item()));

  shouldDisplaySocialLink = computed(
    () => !this.userLogoUrl() && this.sourceLogo() && (!this.isPostPinned() || this.feedType() === FeedType.common)
  );

  isRepost = computed(() => this.item().post_type.slug === PostTypes.repost);

  postTypeIcon = computed((): IconName | '' => {
    const item = this.item();
    const platform = this.originalPlatform();

    if (SOCIAL_PLATFORM_HEADER_TEXTS[platform]) {
      return '';
    }

    switch (item.post_type.slug) {
      case PostTypes.post:
      case PostTypes.postPaid:
        return item.content.videos.length ? POST_TYPE_ICON[PostTypes.postedVideo] : POST_TYPE_ICON[PostTypes.post];
      default:
        return POST_TYPE_ICON[item.post_type.slug] ?? '';
    }
  });

  originalPlatform = computed(() => this.postSocialService.getOriginalPlatform(this.item()));

  showDateInHeader = computed(() => this.isFollowEvent());

  linkTarget = computed(() => (this.openInSameWindow() ? '_self' : '_blank'));

  postTypeText = computed(() => {
    const item = this.item();
    const platform = this.originalPlatform();

    if (SOCIAL_PLATFORM_HEADER_TEXTS[platform]) {
      return SOCIAL_PLATFORM_HEADER_TEXTS[platform];
    }

    switch (item.post_type.slug) {
      case PostTypes.repost:
        return item.content.community_repost?.original?.original_type_text;
      case PostTypes.post:
      case PostTypes.postPaid:
        return item.content.videos.length ? POST_TYPE_TEXT.publishedVideo : POST_TYPE_TEXT.publishedPost;
      default:
        return item.post_type.text;
    }
  });

  onSocialClick(url: string) {
    if (!this.isGuest()) {
      this.gtagCommunityService.socialClick(this.item().post_uuid, this.originalPlatform());
    }

    this.socialClick.emit(url);
  }

  onOwnerTitleClick(): void {
    if (!this.isClickableProfile()) {
      this.sendWebviewProfilePage(this.item().user?.user_uuid);
      return;
    }

    if (this.#navConfig?.useExternalPostNavigation) {
      const homepage = this.#externalPostNavService.getUserHomepageUrl(this.item());
      if (homepage) {
        window.open(homepage, '_blank');
        this.ownerTitleClick.emit();
        return;
      }
    }

    const userUuid = this.isRepost()
      ? this.item().content.community_repost?.source?.source_user?.user_uuid
      : this.item().user?.user_uuid;

    this.sendWebviewProfilePage(userUuid);

    if (this.#externalProfileService) {
      this.#externalProfileService
        .getProfilePageUrl(userUuid)
        .pipe(take(1))
        .subscribe(url => {
          if (url) {
            this.navigationService.openOnSamePage(url);
          }
        });
    }

    this.ownerTitleClick.emit();
  }

  onLogoClick(): void {
    this.onOwnerTitleClick();
    this.logoClick.emit();
  }

  onLogoKeydown(event: KeyboardEvent): void {
    if (this.isClickableProfile() && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.onLogoClick();
    }
  }

  onOwnerTitleKeydown(event: KeyboardEvent): void {
    if (this.isClickableProfile() && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.onOwnerTitleClick();
    }
  }

  private sendWebviewProfilePage(userUuid?: string): void {
    if (!userUuid || !this.mobileEventsService.isWebView) {
      return;
    }

    const username = this.isRepost()
      ? this.item().content.community_repost?.source?.source_user?.username
      : this.item().user?.username;

    const uuid = this.isRepost()
      ? this.item().content.community_repost?.source?.source_user?.user_uuid
      : this.item().user?.user_uuid;

    this.mobileEventsService.openProfilePage({ username, userUuid: uuid });
  }
}
